import { cn } from '@shadcn/lib/utils';
import { cva } from 'class-variance-authority';
import { useMemo } from 'react';

import { Text } from '../../atoms/text/text';

type ThresholdOption = { value: number; label: string };
type ThresholdOptionWithOffset = ThresholdOption & { offset: number; __id: string };
type Appearance = 'primary' | 'aquamarine' | 'dodger' | 'mauve' | 'pink';

type ThresholdBarProps = {
  readonly disabled?: boolean;
  readonly thresholds: ThresholdOption[];
  readonly maxValue: number;
  readonly value: number;
  readonly showAnnotations?: boolean;
  readonly appearance?: Appearance;
  readonly className?: string;
};

const MAX_PERCENTAGE = 100;
const MID_POINT = 2;
const SORT_HIGH = 1;
const SORT_LOW = -1;
const MIN_SEGMENT_WIDTH_PX = 60;
const SEGMENT_WIDTH_DIVISOR = 6;
const MULTI_SEGMENT_WIDTH_FACTOR = 0.8;
const DEFAULT_MAX_VALUE = 100;
const MIN_LEFT_POSITION = 0;
const SINGLE_THRESHOLD_LABEL_CENTER = 50;
const TWO_THRESHOLD_COUNT = 2;

const getOffset = (value: number, max: number) => (value / max) * MAX_PERCENTAGE;

const getThresholdLabelPosition = (targetThresholdIndex: number, thresholds: ThresholdOptionWithOffset[]) => {
  const currentOffset = thresholds[targetThresholdIndex].offset;
  const nextOffset = thresholds[targetThresholdIndex + 1]?.offset ?? MAX_PERCENTAGE;
  const segmentMidpoint = (currentOffset + nextOffset) / MID_POINT;
  return targetThresholdIndex === 0 ? segmentMidpoint : Math.max(segmentMidpoint, MIN_LEFT_POSITION);
};

const isSingleSegment = (thresholds: ThresholdOptionWithOffset[], maxValue: number) => {
  return thresholds.length === TWO_THRESHOLD_COUNT && maxValue === 0;
};

const filledBarVariants = cva('h-full rounded-lg', {
  variants: {
    appearance: {
      aquamarine: 'bg-[var(--color-aquamarine-500)]',
      dodger: 'bg-[var(--color-dodger-500)]',
      mauve: 'bg-[var(--color-mauve-500)]',
      pink: 'var(--color-pink-500)',
      primary: 'bg-indigo-500',
    },
  },
  defaultVariants: { appearance: 'primary' },
});

const FilledBar = ({
  value,
  maxValue,
  appearance,
}: {
  readonly value: number;
  readonly maxValue: number;
  readonly appearance: Appearance;
}) => {
  const effectiveMaxValue = maxValue || DEFAULT_MAX_VALUE;
  return (
    <div
      className={filledBarVariants({ appearance })}
      data-testid="filled-bar"
      style={{ width: `${getOffset(value, effectiveMaxValue)}%` }}
    />
  );
};

const getSegmentWidth = (targetThresholdIndex: number, thresholds: ThresholdOptionWithOffset[]) => {
  const currentThreshold = thresholds[targetThresholdIndex];
  const nextThreshold = thresholds[targetThresholdIndex + 1];
  const rangeStart = targetThresholdIndex === 0 ? 0 : currentThreshold.offset;
  const rangeEnd = nextThreshold?.offset ?? MAX_PERCENTAGE;
  return rangeEnd - rangeStart;
};

const ThresholdLine = ({
  threshold,
  showAnnotations,
  thresholdsWithOffsets,
  maxValue,
}: {
  readonly threshold: ThresholdOptionWithOffset;
  readonly showAnnotations?: boolean;
  readonly thresholdsWithOffsets: ThresholdOptionWithOffset[];
  readonly maxValue: number;
}) => {
  return threshold.value !== 0 ? (
    <div
      className="absolute h-full w-[3px] bg-white"
      data-testid="threshold"
      key={threshold.label}
      style={{ left: `${threshold.offset}%` }}
    >
      {showAnnotations && !isSingleSegment(thresholdsWithOffsets, maxValue) && (
        <Text className="absolute left-1/2 transform -translate-x-1/2 top-[-1.5em] text-center w-max">
          {threshold.value}
        </Text>
      )}
    </div>
  ) : null;
};

const ThresholdLabel = ({
  threshold,
  index,
  thresholdsWithOffsets,
  showAnnotations,
  value,
  maxValue,
}: {
  readonly threshold: ThresholdOptionWithOffset;
  readonly index: number;
  readonly thresholdsWithOffsets: ThresholdOptionWithOffset[];
  readonly showAnnotations?: boolean;
  readonly value: number;
  readonly maxValue: number;
}) => {
  if (!showAnnotations) return null;

  const segmentWidth = getSegmentWidth(index, thresholdsWithOffsets);
  const isSingleSegmentScenario = isSingleSegment(thresholdsWithOffsets, maxValue);

  if (isSingleSegmentScenario && thresholdsWithOffsets.length === TWO_THRESHOLD_COUNT && threshold.value === 0) {
    return null;
  }

  const labelPosition = isSingleSegmentScenario
    ? SINGLE_THRESHOLD_LABEL_CENTER
    : getThresholdLabelPosition(index, thresholdsWithOffsets);

  const maxWidthPercent = isSingleSegmentScenario
    ? MAX_PERCENTAGE
    : Math.max(MIN_SEGMENT_WIDTH_PX / SEGMENT_WIDTH_DIVISOR, segmentWidth * MULTI_SEGMENT_WIDTH_FACTOR);

  return (
    <Text
      className={`absolute mt-3 break-words text-xs leading-tight overflow-hidden text-center transform -translate-x-1/2`}
      data-testid="threshold-label-annotation"
      style={{
        left: `${labelPosition}%`,
        width: `${maxWidthPercent}%`,
        maxHeight: '3rem',
      }}
    >
      {threshold.label}
    </Text>
  );
};

const Threshold = ({
  threshold,
  showAnnotations,
  index,
  thresholdsWithOffsets,
  value,
  maxValue,
}: {
  readonly threshold: ThresholdOptionWithOffset;
  readonly index: number;
  readonly showAnnotations?: boolean;
  readonly thresholdsWithOffsets: ThresholdOptionWithOffset[];
  readonly value: number;
  readonly maxValue: number;
}) => {
  return (
    <>
      <ThresholdLine
        maxValue={maxValue}
        showAnnotations={showAnnotations}
        threshold={threshold}
        thresholdsWithOffsets={thresholdsWithOffsets}
      />
      <ThresholdLabel
        index={index}
        maxValue={maxValue}
        showAnnotations={showAnnotations}
        threshold={threshold}
        thresholdsWithOffsets={thresholdsWithOffsets}
        value={value}
      />
    </>
  );
};

const useThresholds = (thresholds: ThresholdBarProps['thresholds'], maxValue: ThresholdBarProps['maxValue']) => {
  return useMemo(() => {
    const withIds = thresholds.map((t, i) => ({ ...t, __id: `${i}-${t.label}-${t.value}` }));
    const effectiveMaxValue = maxValue || Math.max(...thresholds.map((t) => t.value)) || DEFAULT_MAX_VALUE;
    return [...withIds]
      .sort((a, b) => (a.value > b.value ? SORT_HIGH : SORT_LOW))
      .map((t) => ({ ...t, offset: getOffset(t.value, effectiveMaxValue) }));
  }, [maxValue, thresholds]);
};

const thresholdVariants = cva('relative w-full h-[9px] rounded-lg', {
  variants: {
    appearance: {
      disabled: 'bg-neutral-600',
      primary: 'bg-neutral-400',
    },
  },
  defaultVariants: { appearance: 'primary' },
});

const ThresholdBarContent = ({
  disabled,
  maxValue,
  value,
  showAnnotations,
  thresholdsWithOffsets,
  appearance,
}: {
  readonly disabled: boolean;
  readonly maxValue: number;
  readonly value: number;
  readonly showAnnotations?: boolean;
  readonly thresholdsWithOffsets: ThresholdOptionWithOffset[];
  readonly appearance: Appearance;
}) => {
  return (
    <div className={thresholdVariants({ appearance: disabled ? 'disabled' : 'primary' })}>
      {!disabled && (
        <>
          <div className="absolute w-full h-full rounded-lg overflow-x-hidden">
            <FilledBar appearance={appearance} maxValue={maxValue} value={value} />
          </div>
          {thresholdsWithOffsets.map((threshold, index) => (
            <Threshold
              index={index}
              key={threshold.__id}
              maxValue={maxValue}
              showAnnotations={showAnnotations}
              threshold={threshold}
              thresholdsWithOffsets={thresholdsWithOffsets}
              value={value}
            />
          ))}
        </>
      )}
    </div>
  );
};

const ThresholdBar = ({
  disabled = false,
  thresholds,
  maxValue,
  value,
  showAnnotations,
  className,
  appearance = 'primary',
}: ThresholdBarProps) => {
  const thresholdsWithOffsets = useThresholds(thresholds, maxValue);

  return (
    <div
      className={cn('relative w-full', { 'pb-12 mb-2': showAnnotations && thresholds.length > 0 }, className)}
      data-testid="threshold-bar"
    >
      <ThresholdBarContent
        appearance={appearance}
        disabled={disabled}
        maxValue={maxValue}
        showAnnotations={showAnnotations}
        thresholdsWithOffsets={thresholdsWithOffsets}
        value={value}
      />
    </div>
  );
};

export { ThresholdBar, type ThresholdBarProps };
